import { Controller } from '@hotwired/stimulus'

// Connects to data-controller="floating-actions"
//
// Brings back the list's action bar as a floating pill once the real one has scrolled out of
// reach. Two targets: `sentinel` is the action bar itself, `bar` is the pill. The pill is styled
// entirely in CSS off a `data-visible` attribute, which is the only thing this controller writes.
//
// The trigger is an IntersectionObserver on the sentinel, with a negative top rootMargin equal to
// the sticky header's height so that "out of reach" means hidden behind the header rather than
// merely off the top of the viewport.
//
// A sentinel that has not been reached yet and one that has been scrolled past both report
// isIntersecting === false, so that flag alone would show the pill on a page sitting at the top
// with a tall enough hero. Comparing the sentinel's top against the root's tells the two apart.
export default class extends Controller {
  static targets = ['sentinel', 'bar']

  // Turbo caches a clone of the body on navigation, and the pill's visibility is scroll state.
  // Drop the attribute before it does: a snapshot cached while scrolled down is painted as an
  // instant preview on the next visit — at the top of the page, where the real action bar is
  // already on screen — and the pill would flash beside it until the observer corrects it.
  #resetForCache = () => this.barTarget.removeAttribute('data-visible')

  connect() {
    if (!this.hasSentinelTarget || !this.hasBarTarget) return

    document.addEventListener('turbo:before-cache', this.#resetForCache)

    // No viewport guard here: the pill is `hidden lg:flex`, so toggling the attribute below the lg
    // breakpoint changes nothing on screen — whereas checking matchMedia on connect alone would
    // leave a window resized from mobile to desktop without an observer at all. Below lg the
    // header is hidden and this reads 0, which is the right offset for that viewport anyway.
    const headerHeight = document.querySelector('header.sticky')?.offsetHeight ?? 0

    this.observer = new IntersectionObserver(
      ([entry]) => {
        const underHeader = entry.boundingClientRect.top < (entry.rootBounds?.top ?? headerHeight)
        this.barTarget.toggleAttribute('data-visible', !entry.isIntersecting && underHeader)
      },
      { rootMargin: `-${headerHeight}px 0px 0px 0px` }
    )
    this.observer.observe(this.sentinelTarget)
  }

  disconnect() {
    document.removeEventListener('turbo:before-cache', this.#resetForCache)
    this.observer?.disconnect()
  }
}
